import { Check } from 'lucide-react'
import NavigationButtons from './NavigationButtons'

function ThemeNavigation({ themes, currentThemeIndex, scores, onThemeChange, onComplete, onBack, onScrollToFirstUnanswered }) {
  const currentTheme = themes[currentThemeIndex]
  
  // Check if current theme is completed
  const currentThemeQuestions = currentTheme.questions
  const currentThemeScores = currentThemeQuestions.map(q => scores[q.id])
  
  // Calculate overall completion
  const totalQuestions = themes.reduce((total, theme) => total + theme.questions.length, 0)
  const completedQuestions = Object.values(scores).filter(score => score !== null && score !== undefined).length
  const overallCompletion = Math.round((completedQuestions / totalQuestions) * 100)

  const getThemeStatus = (themeIndex) => {
    const theme = themes[themeIndex]
    const themeQuestions = theme.questions
    const themeScores = themeQuestions.map(q => scores[q.id])
    const completed = themeScores.filter(score => score !== null && score !== undefined).length
    
    if (completed === themeQuestions.length) return 'completed'
    if (completed > 0) return 'partial'
    return 'pending'
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
      {/* Overall Progress */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xl font-bold text-cedec-blue">Progression générale</h2>
          <span className="text-sm font-medium text-gray-600">{completedQuestions}/{totalQuestions} questions</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div 
            className="bg-gradient-to-r from-cedec-blue to-blue-600 h-3 rounded-full transition-all duration-500"
            style={{ width: `${overallCompletion}%` }}
          ></div>
        </div>
        <div className="text-center text-sm text-gray-600 mt-1">
          {overallCompletion}% complété
        </div>
      </div>

      {/* Theme Navigation */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {themes.map((theme, index) => {
          const status = getThemeStatus(index)
          const isCurrent = index === currentThemeIndex
          
          return (
            <button
              key={theme.id}
              onClick={() => onThemeChange(index)}
              className={`p-3 rounded-lg border-2 transition-all duration-300 text-center ${
                isCurrent 
                  ? 'border-cedec-blue bg-blue-50 shadow-md' 
                  : status === 'completed'
                  ? 'border-green-500 bg-green-50 hover:bg-green-100'
                  : status === 'partial'
                  ? 'border-orange-400 bg-orange-50 hover:bg-orange-100'
                  : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
              }`}
            >
              <div className="text-2xl mb-1">{theme.icon}</div>
              <div className={`text-xs font-medium ${
                isCurrent ? 'text-cedec-blue' : 'text-gray-700'
              }`}>
                {theme.title}
              </div>
              {status === 'completed' && (
                <Check className="w-4 h-4 text-green-600 mx-auto mt-1" />
              )}
              {status === 'partial' && (
                <div className="text-xs text-orange-600 mt-1">
                  {theme.questions.map(q => scores[q.id]).filter(s => s !== null && s !== undefined).length}/{theme.questions.length}
                </div>
              )}
            </button>
          )
        })}
      </div>

      {/* Current Theme Info */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 mb-6 border-l-4 border-cedec-blue">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-3xl">{currentTheme.icon}</span>
          <div>
            <h3 className="text-lg font-semibold text-gray-800">{currentTheme.title}</h3>
            <p className="text-sm text-gray-600">
              {currentThemeQuestions.length} questions • Score maximum: {currentTheme.maxScore} points
            </p>
          </div>
        </div>
        
        {/* Theme Progress */}
        <div className="mt-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium text-gray-700">Progression du thème</span>
            <span className="text-sm text-gray-600">
              {currentThemeScores.filter(s => s !== null && s !== undefined).length}/{currentThemeQuestions.length}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-cedec-blue h-2 rounded-full transition-all duration-300"
              style={{ 
                width: `${(currentThemeScores.filter(s => s !== null && s !== undefined).length / currentThemeQuestions.length) * 100}%` 
              }}
            ></div>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <NavigationButtons
        themes={themes}
        currentThemeIndex={currentThemeIndex}
        scores={scores}
        onThemeChange={onThemeChange}
        onComplete={onComplete}
        onBack={onBack}
        onScrollToFirstUnanswered={onScrollToFirstUnanswered}
      />
    </div>
  )
}

export default ThemeNavigation
